import { useCallback, useEffect, useRef, useState } from "react";
import { getUniqueId } from "@jugl-web/utils";
import { useRestApiProvider } from "../../../RestApiProvider";
import {
  UsePaginatedRequestHookParams,
  UsePaginatedRequestHookReturn,
} from "../../../types";
import { KlipyGifModel } from "../models";

export type UseKlipyGifsParams = UsePaginatedRequestHookParams<{
  apiKey: string;
  customerId: string;
  query?: string;
}>;

export type UseKlipyGifsReturn = UsePaginatedRequestHookReturn<KlipyGifModel>;

export const useKlipyGifs = ({
  apiKey,
  customerId,
  query = "",
  pageSize = 24,
  skip = false,
}: UseKlipyGifsParams): UseKlipyGifsReturn => {
  const { chatsApi } = useRestApiProvider();
  const requestId = useRef<string>(undefined);

  const [items, setItems] = useState<KlipyGifModel[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [hasNext, setHasNext] = useState(false);
  const [isError, setIsError] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const [fetchGifs] = chatsApi.useLazyGetKlipyGifsQuery();

  const loadGifs = useCallback(
    async (page: number) => {
      if (!apiKey || !customerId) {
        setIsError(true);
        setIsLoading(false);
        return;
      }

      const newRequestId = getUniqueId();
      requestId.current = newRequestId;

      try {
        setIsError(false);
        setIsLoading(true);

        const result = await fetchGifs({
          apiKey,
          customerId,
          page,
          pageSize,
          query: query.trim() || undefined,
          // Third-party (Klipy) failures are rendered inline by the picker
          meta: { silent: true },
        }).unwrap();

        if (requestId.current !== newRequestId) return;

        setItems((prev) => {
          if (page === 1) return result.data;
          // Klipy can repeat an id across pages while paginating
          const seen = new Set(prev.map((item) => item.id));
          return [...prev, ...result.data.filter((item) => !seen.has(item.id))];
        });
        setHasNext(result.has_next);
        setCurrentPage(page);
      } catch {
        if (requestId.current !== newRequestId) return;
        setIsError(true);
      } finally {
        // A superseded request must not clear the loading flag of the live one
        if (requestId.current === newRequestId) {
          setIsLoading(false);
        }
      }
    },
    [apiKey, customerId, pageSize, query, fetchGifs]
  );

  const loadMore = useCallback(async () => {
    if (isLoading || !hasNext) return;
    await loadGifs(currentPage + 1);
  }, [isLoading, hasNext, currentPage, loadGifs]);

  const reset = useCallback(() => {
    setItems([]);
    setCurrentPage(1);
    setHasNext(false);
    setIsError(false);
    loadGifs(1);
  }, [loadGifs]);

  const retry = useCallback(async () => {
    const pageToRetry =
      currentPage === 1 && items.length === 0 ? 1 : currentPage + 1;
    await loadGifs(pageToRetry);
  }, [currentPage, items.length, loadGifs]);

  useEffect(() => {
    if (skip) return;
    reset();
  }, [apiKey, customerId, query, skip, reset]);

  const isLoadingMore = isLoading && items.length > 0;
  // `currentPage` only advances on success, so it can't distinguish an initial
  // failure from a load-more failure — the presence of items can.
  const isLoadingMoreError = isError && items.length > 0;

  return {
    data: items,
    isLoading,
    isLoadingMore,
    isError,
    isLoadingMoreError,
    hasMore: hasNext,
    reset,
    loadMore,
    retry,
  };
};
